import { HttpEvent, HttpHandler, HttpRequest, HttpStatusCode } from '@angular/common/http';
import { inject, Injectable, Injector } from '@angular/core';

import { adaptApiError, AppError, DISABLE_ERROR_INTERCEPTOR, ErrorsInterceptor } from '@hglobal/fin-ui';
import { catchError, Observable, throwError } from 'rxjs';

import { isBusinessApiUrl } from '@zeusm/shared/core/helpers';
import { GlobalDialogService } from '@zeusm/shared/core/services';
import { AuthService } from '@zeusm/shared/session/auth';

import { ContactSupportDialog, ContactSupportDialogComponent, VpnCheckDialogComponent } from '../../../common';
import { RouterStore } from '../../../store';

interface ErrorFields {
  codeField: string;
  messageField: string;
  dataField: string;
}

const PERSONAL_API_ERROR_FIELDS: ErrorFields = {
  codeField: 'code',
  messageField: 'message',
  dataField: 'details',
};

const BUSINESS_API_ERROR_FIELDS: ErrorFields = {
  codeField: 'error_code',
  messageField: 'error_description',
  dataField: 'details',
};

/**
 * App Errors interceptor
 */
@Injectable()
export class AppErrorsInterceptor extends ErrorsInterceptor {
  readonly #injector = inject(Injector);
  readonly #routerStore = inject(RouterStore);
  readonly #dialogService = inject(GlobalDialogService);
  readonly #contactSupportDialog = inject(ContactSupportDialog);

  #vpnDialogShown = false;
  override codeField = PERSONAL_API_ERROR_FIELDS.codeField;
  override messageField = PERSONAL_API_ERROR_FIELDS.messageField;
  override dataField = PERSONAL_API_ERROR_FIELDS.dataField;

  override intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    if (request.context.get(DISABLE_ERROR_INTERCEPTOR)) {
      return next.handle(request);
    }

    const errorFields = isBusinessApiUrl(request.url) ? BUSINESS_API_ERROR_FIELDS : PERSONAL_API_ERROR_FIELDS;

    return next.handle(request).pipe(
      adaptApiError(errorFields),
      catchError((error: AppError) => throwError(() => this.beforeThrowing(error))),
    );
  }

  override beforeThrowing(error: AppError): AppError {
    if (error.originalError?.status === HttpStatusCode.Unauthorized) {
      this.#getAuthService().clearAuthState();
    }

    if (error.originalError?.status === HttpStatusCode.NotFound) {
      this.#routerStore.navigateTo404();
    }

    if (error.originalError?.status === HttpStatusCode.Forbidden) {
      const url = error.originalError?.url || '';

      if (url.includes('/backoffice/')) {
        this.#showVpnCheckDialog();
      }

      if (url.includes('/me')) {
        this.#contactSupportDialog.open(ContactSupportDialogComponent, {
          data: { message: 'shared.core.accountNotReady.text' },
        });
      }

      if (error?.code === 'INACTIVE_USER' || error?.code === 'USER_NOT_FOUND') {
        const errorMessageMap: Record<string, string> = {
          INACTIVE_USER: 'shared.core.accountInactive.text',
        };
        const message = errorMessageMap[error?.code as string] ?? 'shared.core.accountNotReady.text';

        this.#contactSupportDialog.open(ContactSupportDialogComponent, {
          data: { message },
        });
        this.#getAuthService().clearAuthState();
      }
    }

    return error;
  }

  /** Lazy-loaded AuthService to avoid circular dependency */
  #getAuthService(): AuthService {
    return this.#injector.get(AuthService);
  }

  #showVpnCheckDialog(): void {
    if (this.#vpnDialogShown) {
      return;
    }

    this.#vpnDialogShown = true;

    const dialogRef = this.#dialogService.open<VpnCheckDialogComponent, void>({
      components: [VpnCheckDialogComponent],
      isOnTop: true,
    });

    dialogRef.afterClosed().subscribe(() => {
      this.#vpnDialogShown = false;
    });
  }
}
